import { useEffect, useState } from "react";
import { useNewField, isOverlapCancel } from "../../hooks/useNewField";
import { useUiStore } from "../../stores/uiStore";
import { isSupportedBoundaryFile } from "../../lib/files";
import { Modal } from "../../ui/Modal";

// Drag a boundary file ANYWHERE on the screen to create a new field (Front-End.md §A1).
export function GlobalDropZone() {
  const { fromFile } = useNewField();
  const [dragging, setDragging] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  // While the Bulk Data Dump is open, a dropped file is BULK DATA, not a new field's boundary — the
  // global create-field dropzone stands down completely — it fired alongside the bul flow and ran
  // ingest-boundary on a multi-field shapefile before Submit was ever pressed).
  const bulkOpen = useUiStore((s) => s.bulkDump != null);

  useEffect(() => {
    if (bulkOpen) {
      setDragging(false);
      // standing down must not surrender the WINDOW: with no dragover preventDefault anywhere, a
      // drop that misses the bulk panel's target NAVIGATES the browser to the file — destroying the
      // whole mid-flow state (manifest, attach exclusions).
      const swallowOver = (e: DragEvent) => {
        if (Array.from(e.dataTransfer?.types ?? []).includes("Files"))
          e.preventDefault();
      };
      const swallowDrop = (e: DragEvent) => e.preventDefault();
      window.addEventListener("dragover", swallowOver);
      window.addEventListener("drop", swallowDrop);
      return () => {
        window.removeEventListener("dragover", swallowOver);
        window.removeEventListener("drop", swallowDrop);
      };
    }
    let depth = 0;
    const hasFiles = (e: DragEvent) =>
      Array.from(e.dataTransfer?.types ?? []).includes("Files");
    const onEnter = (e: DragEvent) => {
      if (hasFiles(e)) {
        depth++;
        setDragging(true);
      }
    };
    const onOver = (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault();
    };
    const onLeave = () => {
      depth = Math.max(0, depth - 1);
      if (!depth) setDragging(false);
    };
    const onDrop = async (e: DragEvent) => {
      e.preventDefault();
      depth = 0;
      setDragging(false);
      const file = e.dataTransfer?.files?.[0];
      if (!file) return;
      if (!isSupportedBoundaryFile(file.name)) {
        setMsg(
          `Unsupported file “${file.name}”. Use .geojson, .zip (shapefile), .kml or .kmz.`,
        );
        setTimeout(() => setMsg(null), 4500);
        return;
      }
      setMsg(`Creating field from ${file.name}…`);
      let hold = 3500;
      try {
        const f = await fromFile(file);
        // uploaded shp/kml/kmz boundaries parse async → boundary/acres land shortly
        if (f.has_boundary) {
          setMsg(`Created “${f.field_name}”`);
        } else {
          setMsg(`Created “${f.field_name}” — processing boundary…`);
          hold = 7000;
        }
      } catch (err) {
        // a DECLINED overlap is a user choice, not a failure — say nothing.
        if (isOverlapCancel(err)) {
          setMsg(null);
          return;
        }
        setMsg((err as Error).message);
        hold = 6000;
      }
      setTimeout(() => setMsg(null), hold);
    };
    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragover", onOver);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("drop", onDrop);
    };
  }, [fromFile, bulkOpen]);

  return (
    <>
      {dragging && (
        <Modal>
          <div className="pointer-events-none fixed inset-0 z-overlay flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="rounded-lg border-2 border-dashed border-white/60 bg-surface/95 px-6 py-4 text-sm font-medium text-fg">
              Drop a boundary file to create a new field
            </div>
          </div>
        </Modal>
      )}
      {msg && (
        <div className="fixed bottom-3 left-3 z-dialog rounded-md border border-line bg-raised px-3 py-2 text-xs text-fg shadow-md">
          {msg}
        </div>
      )}
    </>
  );
}
